// ============================================================================
// Géométrie déductive du noyau (lot 5.4) : les RÈGLES (paquet JSON).
// ============================================================================
// Une règle = une propriété du cours : ses conditions (`if`), ce qu'elle donne
// (`then`), parfois une condition sur les longueurs connues (`when` : le plus grand
// côté, pour la contraposée de Pythagore). Les motifs s'écrivent comme les lignes de
// l'élève, avec des points variables « ?A » et des droites variables « (?d) » ; une
// relation entre longueurs écrit ses segments « ?A?B ».
//
// Le chargement est TOUT OU RIEN : un motif illisible, une variable de la conclusion
// absente des conditions, un exemple mal formé → le paquet est refusé. Les exemples
// (énoncé, conclusion, figure) sont rejoués par `selfTestGeometryPack` (tests).
// ============================================================================

import { looksGeometric, parseGProp, gpropPoints, gpropLineNames, type GProp } from './props';
import pack from './packs/geometry-core.json';

export interface GeoExample {
  facts: string[];
  proves: string;
  points: Record<string, [number, number]>;
  lines?: Record<string, [[number, number], [number, number]]>;
}
export interface GeoRuleSpec {
  id: string;
  version: number;
  label: string;
  /** la SKILLCARD de la propriété (affichée avec la ligne validée) */
  card: string;
  if: string[];
  then: string[];
  when?: { longest: [string, string, string] }[];
  levels?: string[];
  examples: GeoExample[];
}
export interface GeoPackSpec {
  id: string;
  version: number;
  schema: 1;
  label: string;
  rules: GeoRuleSpec[];
}

/** un motif : une proposition, ou une relation entre longueurs (gardée en texte) */
export type Pattern = { k: 'prop'; p: GProp; text: string } | { k: 'metric'; text: string };
export interface GeoRule {
  /** identifiant qualifié : « geometry.thales@1 » */
  qid: string;
  id: string;
  label: string;
  card: string;
  hyps: Pattern[];
  concl: Pattern[];
  when: { longest: [string, string, string] }[];
  spec: GeoRuleSpec;
}

const VAR_RE = /\?[A-Z][0-9]*/g;
function patternOf(text: string): Pattern {
  if (looksGeometric(text)) return { k: 'prop', p: parseGProp(text, true), text };
  if (!/[=≠]/.test(text)) throw new Error(`motif sans relation : ${text}`);
  return { k: 'metric', text };
}
export function patternVars(p: Pattern): string[] {
  if (p.k === 'metric') return p.text.match(VAR_RE) ?? [];
  return [...gpropPoints(p.p), ...gpropLineNames(p.p)].filter((v) => v.startsWith('?'));
}
/** ordre de recherche : propositions d'abord (les « ∈ (droite) » ensuite), longueurs à la fin */
function rank(p: Pattern): number {
  if (p.k === 'metric') return 2;
  return p.p.k === 'online' ? 1 : 0;
}

export function loadGeometryPack(spec: GeoPackSpec): GeoRule[] {
  if (spec.schema !== 1) throw new Error('paquet de géométrie : schéma inconnu');
  const ids = new Set<string>();
  return spec.rules.map((r) => {
    if (!/^[a-z][a-z0-9_]*$/.test(r.id) || ids.has(r.id))
      throw new Error(`règle de géométrie : identifiant invalide ou en double (${r.id})`);
    ids.add(r.id);
    const hyps = r.if.map(patternOf).sort((a, b) => rank(a) - rank(b));
    const concl = r.then.map(patternOf);
    if (!hyps.length || !concl.length)
      throw new Error(`règle ${r.id} : conditions ou conclusion vides`);
    const bound = new Set(hyps.flatMap(patternVars));
    const free = concl.flatMap(patternVars).filter((v) => !bound.has(v));
    // une conclusion structurelle lie ses variables par unification avec la cible
    if (free.length && concl.some((c) => c.k === 'metric'))
      throw new Error(`règle ${r.id} : variables libres dans la conclusion (${free.join(', ')})`);
    for (const w of r.when ?? [])
      for (const s of w.longest)
        for (const v of s.match(VAR_RE) ?? [])
          if (!bound.has(v) && !concl.flatMap(patternVars).includes(v))
            throw new Error(`règle ${r.id} : variable ${v} inconnue dans « when »`);
    if (!r.examples.length) throw new Error(`règle ${r.id} : au moins un exemple`);
    return {
      qid: `geometry.${r.id}@${r.version}`,
      id: r.id,
      label: r.label,
      card: r.card,
      hyps,
      concl,
      when: r.when ?? [],
      spec: r,
    };
  });
}

let RULES: GeoRule[] | null = null;
/** Le paquet geometry-core@1, chargé une fois. */
export function geometryRules(): readonly GeoRule[] {
  RULES ??= loadGeometryPack(pack as unknown as GeoPackSpec);
  return RULES;
}
export const GEOMETRY_PACK_ID = `${(pack as { id: string }).id}@${(pack as { version: number }).version}`;
